import React, { useState } from "react";
import fetchData from "../api/fetchApi";
import {
  UploadCloud,
  FileText,
  X,
  ChevronLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import CategoriesList from "./common/CategoriesList";

const AddArticlePage = () => {
  const [files, setFiles] = useState([]);
  const [fileUuids, setFileUuids] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryUuid, setCategoryUuid] = useState(null);
  React.useEffect(() => {
    // جلب التصنيفات عند التحميل الأولي
    async function loadCategories() {
      try {
        const res = await fetchData("admin/law-categories");
        setCategories(res);
      }
      catch (err) {
        console.error("فشل في جلب التصنيفات");
      }
    }
    loadCategories();
  }, []);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const navigate = useNavigate();

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    setUploading(true);
    setUploadError(null);

    // أضف الملفات الجديدة للحالة
    const newFiles = selectedFiles.map((file) => ({
      file,
      progress: 0,
      uuid: null,
      uploading: true,
      error: null,
      info: null,
    }));

    setFiles((prev) => [...prev, ...newFiles]);

    // ارفع الملفات وحدة وحدة
    for (const fileObj of newFiles) {
      try {
        const formData = new FormData();
        formData.append("file", fileObj.file);

        // مبدئيًا نحدث التقدم
        setFiles((prev) =>
          prev.map((f) =>
            f === fileObj ? { ...f, progress: 50 } : f
          )
        );

        let res = await fetchData("upload", "POST", formData);

        

        // نضمن أن النتيجة مصفوفة دائمًا
        const results = Array.isArray(res) ? res : [res];

        // نحدث حالة الملف الحالي
        setFiles((prev) =>
          prev.map((f) =>
            f === fileObj
              ? {
                  ...f,
                  uploading: false,
                  progress: 100,
                  info: results[0],
                  uuid: results[0].uuid,
                }
              : f
          )
        );

        // نضيف الـ UUIDs للـ state
        setFileUuids((prev) => [
          ...prev,
          ...results.map((r) => r.uuid),
        ]);
      } catch (err) {
        setFiles((prev) =>
          prev.map((f) =>
            f === fileObj
              ? { ...f, error: "فشل رفع الملف", uploading: false }
              : f
          )
        );
        setUploadError("حدث خطأ أثناء رفع بعض الملفات");
      }
    }

    setUploading(false);
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setFileUuids((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);

    if (!title.trim() || !content.trim()) {
      setSubmitError("يرجى تعبئة العنوان والمحتوى");
      return;
    }

    const payload = {
      title: title.trim(),
      content: content.trim(),
      file_uuids: fileUuids,
      category_uuid: categoryUuid,
    };

    try {
      await fetchData("admin/blog", "POST", JSON.stringify(payload));
      setSubmitSuccess(true);
      setTitle("");
      setContent("");
      setFiles([]);
      setFileUuids([]);
      setTimeout(() => setSubmitSuccess(false), 2000);
    } catch (err) {
      setSubmitError("فشل في إضافة المقال");
    }
  };

  const handleGoBack = () => navigate(-1);

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen" dir="rtl">
      <div className="flex items-center mb-6 gap-3">
        <button
          onClick={handleGoBack}
          className="p-2 rounded-full hover:bg-gray-200 transition-colors"
        >
          <ChevronLeft
            size={24}
            className="text-gray-600 transform rotate-180"
          />
        </button>
        <h1 className="text-xl md:text-2xl font-bold text-[#1D2035]">
          اضافة مقال جديد
        </h1>
      </div>

      {/* فقط لعرض النتائج أثناء التطوير */}
      <pre className="bg-white p-2 rounded mb-4 text-xs overflow-auto">
        {JSON.stringify(fileUuids, null, 2)}
      </pre>

      <form
        className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8"
        onSubmit={handleSubmit}
      >
        {/* رفع الملفات */}
        <div
          className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center mb-6 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => document.getElementById("file-upload").click()}
        >
          <div className="flex justify-center items-center">
            <div className="w-12 h-12 rounded-full bg-red-100 grid place-items-center">
              <UploadCloud
                size={28}
                className="text-[var(--Primary-Primary)]"
              />
            </div>
          </div>
          <p className="mt-4 text-base font-semibold text-gray-800">
            اختر ملفات للرفع
          </p>
          <p className="text-sm text-gray-500 mt-1">
            يمكنك رفع صور أو ملفات PDF أو فيديوهات (حتى 50 ميجابايت).
          </p>
          <input
            type="file"
            className="hidden"
            id="file-upload"
            onChange={handleFileChange}
            accept="image/*,application/pdf,video/*"
            multiple
          />
        </div>

        {/* عرض الملفات */}
        {files.length > 0 && (
          <div className="border border-gray-200 rounded-lg p-3 my-6 bg-gray-50 flex flex-col gap-2">
            {files.map((f, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between gap-2"
              >
                <div className="flex items-center gap-3">
                  {f.info?.thumbnail_url ? (
                    <img
                      src={f.info.thumbnail_url}
                      alt="thumb"
                      className="w-12 h-12 rounded object-cover border"
                    />
                  ) : (
                    <FileText
                      className="text-[var(--Primary-Primary)]"
                      size={24}
                    />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {f.info?.name || f.file.name}
                    </p>
                    {f.uploading ? (
                      <p className="text-xs text-gray-500">
                        تحميل... {Math.round(f.progress)}%
                      </p>
                    ) : f.error ? (
                      <p className="text-xs text-red-600">{f.error}</p>
                    ) : f.info?.url ? (
                      <a
                        href={f.info.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 underline"
                      >
                        عرض الملف
                      </a>
                    ) : (
                      <p className="text-xs text-green-600">تم الرفع</p>
                    )}
                  </div>
                </div>

                <div className="mt-2 h-1 w-24 bg-gray-200 rounded-full">
                  <div
                    className="h-1 bg-[var(--Primary-Primary)] rounded-full transition-all duration-300"
                    style={{ width: `${f.progress}%` }}
                  ></div>
                </div>

                <button
                  type="button"
                  onClick={() => removeFile(idx)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>
            ))}
          </div>
        )}

       
      
           
    <div className="space-y-6">
          <div>
            <label
              htmlFor="article-title"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              عنوان المقال
            </label>
            <input
              type="text"
              id="article-title"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
              placeholder="اضافة عنوان جديد"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div>
            <label
              htmlFor="article-content"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              محتوى المقال
            </label>
            <textarea
              id="article-content"
              rows="10"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-0"
              placeholder="..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
            <CategoriesList
                                   value={categoryUuid}
                                   onChange={setCategoryUuid}
                                   categories={categories}
                                 />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-4 border-t pt-6 mt-6">
          <button
            type="button"
            className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-800 font-semibold hover:bg-gray-100 transition-colors"
            onClick={handleGoBack}
          >
            الغاء
          </button>
          <button
            type="submit"
            className="px-6 py-2.5 bg-[var(--Primary-Primary)] text-white rounded-lg font-semibold shadow-sm hover:bg-red-700 transition-colors"
            disabled={uploading}
          >
            {uploading ? "جاري رفع الملفات..." : "إضافة المقال"}
          </button>
        </div>

        {/* Error / Success Messages */}
        {submitError && (
          <div className="text-red-600 text-sm mt-2">{submitError}</div>
        )}
        {submitSuccess && (
          <div className="text-green-600 text-sm mt-2">
            تمت إضافة المقال بنجاح
          </div>
        )}
        {uploadError && (
          <div className="text-red-600 text-sm mt-2">{uploadError}</div>
        )}
      
      </form>
   
    </div>
  );
};

export default AddArticlePage;
